import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { FiltersGrid, Pagination } from '../types';
import { infiniteQueryOptions } from '../utils';
import {
  TransportOrganization,
  TransportOrganizationQuery,
  TransportOrganizationRequestBody,
} from './types';

export const transportOrganizationApi = createApi({
  reducerPath: 'transportOrganizationApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/transport-organizations`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  tagTypes: ['TransportOrganization', 'TransportOrganizations'],
  endpoints: builder => ({
    getTransportOrganizationsGridifyI: builder.infiniteQuery<
      TransportOrganizationQuery[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['TransportOrganization', 'TransportOrganizations'],
    }),
    getTransportOrganizationById: builder.query<TransportOrganization, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['TransportOrganization', 'TransportOrganizations'],
    }),
    createTransportOrganization: builder.mutation<
      TransportOrganization,
      TransportOrganizationRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'TransportOrganizations',
        { type: 'TransportOrganization', id: result?.id },
      ],
    }),
    updateTransportOrganization: builder.mutation<
      TransportOrganization,
      TransportOrganizationRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'TransportOrganizations',
        { type: 'TransportOrganization', id: result?.id },
      ],
    }),
    deleteTransportOrganizationById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'TransportOrganizations',
        { type: 'TransportOrganization', id },
      ],
    }),
  }),
});

export const {
  useGetTransportOrganizationByIdQuery,
  useCreateTransportOrganizationMutation,
  useUpdateTransportOrganizationMutation,
} = transportOrganizationApi;

export const useGetTransportOrganizationsGridifyInfiniteQuery =
  normalizeInfiniteQueryData(
    transportOrganizationApi.useGetTransportOrganizationsGridifyIInfiniteQuery,
  );
